// Dark Store — CMO executive "Receiving & Racks" (tl-app → partner-apis-cs).
// Base path `/leader/receiving`, mirrors the api.js base URL (already ends in
// `/api`). Contract: docs/cp-features/darkstore-api-contract.md §2 in the
// citymall-services repo — that file is the source of truth for these shapes.
//
// Every response uses the house envelope `{ status, data }`; callers should
// read `response.data.data`, same as `getOutForDeliveryDbDetails` does for
// `leader/db-manage/ofd-db-by-id`.

import { api } from "../api";

/**
 * @typedef {Object} RackRef
 * @property {number} rack_id
 * @property {string} rack_no
 * @property {string} rack_name
 */

/**
 * @typedef {Object} ReceivingTp
 * @property {string} cx_id
 * @property {string} cx_name
 * @property {string} area
 * @property {string} date YYYY-MM-DD (IST) the card's orders reached the CMO
 * @property {number} shipment_count
 * @property {number} racked_count
 * @property {"TO_RACK"|"IN_PROGRESS"|"RACKED"} status
 * @property {RackRef[]} racks
 * @property {boolean} labels_missing
 */

/**
 * @typedef {Object} ReceivingTpsResponse
 * @property {{to_rack: number, racked: number, all: number}} tabs
 * @property {number} older_unracked_count
 * @property {ReceivingTp[]} tps
 */

/**
 * @typedef {Object} ReceivingShipment
 * @property {string} shipment_id
 * @property {string} parent_shipment_id
 * @property {string} label_status
 * @property {"RACKED"|"PENDING"} state
 * @property {(RackRef|null)} rack
 */

/**
 * @typedef {Object} ReceivingRosterRack
 * @property {number} rack_id
 * @property {string} rack_no
 * @property {string} rack_name
 * @property {number} count
 * @property {boolean} is_full
 */

/**
 * @typedef {Object} ReceivingTpDetail
 * @property {string} cx_id
 * @property {string} cx_name
 * @property {string} area
 * @property {"TO_RACK"|"IN_PROGRESS"|"RACKED"} status
 * @property {number} scanned
 * @property {number} total
 * @property {ReceivingShipment[]} shipments
 * @property {ReceivingRosterRack[]} racks
 */

/**
 * `GET /api/leader/receiving/tps` — the Receiving list + the three tab counts.
 * `status` is `TO_RACK` (also returns `IN_PROGRESS`), `RACKED` or `ALL`.
 * @param {{status?: "TO_RACK"|"RACKED"|"ALL", search?: string}} params
 * @returns {Promise<{data: {status: string, data: ReceivingTpsResponse}}>}
 */
export const getReceivingTps = ({
  status = "TO_RACK",
  search = "",
  bucket = null,
} = {}) =>
  api.get("/leader/receiving/tps", {
    params: bucket ? { bucket, search } : { status, search },
  });

/**
 * `GET /api/leader/receiving/hub` — racking hub counts (multi-EDD §9):
 * `{ today: {date, to_rack, racked, total}, advance: {to_rack, racked, total,
 * dates: [{date, tps}]}, pending: {to_rack, racked, total} }`.
 */
export const getReceivingHub = () => api.get("/leader/receiving/hub");

/**
 * `GET /api/leader/receiving/tps/:cx_id?date=` — the scan screens' roster
 * for one date card.
 * @param {{cx_id: string, date?: string}} params
 * @returns {Promise<{data: {status: string, data: ReceivingTpDetail}}>}
 */
export const getReceivingTpDetail = ({ cx_id, date }) =>
  api.get(`/leader/receiving/tps/${cx_id}`, { params: { date } });

/**
 * `GET /api/leader/receiving/racks/free` — free racks of this CMO, sorted by
 * `rack_no`.
 * @returns {Promise<{data: {status: string, data: {racks: Array<RackRef & {rack_code: string}>}}}>}
 */
export const getFreeRacks = () => api.get("/leader/receiving/racks/free");

/**
 * `POST /api/leader/receiving/racks/resolve` — a scanned QR (`rack_code`) or
 * a typed `rack_no`. With `cx_id`, a rack still holding another customer's
 * boxes is refused here (400 RACK_OTHER_CX — 1 rack : 1 customer) instead of
 * on the first box scan. Backends without the check ignore `cx_id`.
 * @param {{code: string, cx_id?: string}} payload
 * @returns {Promise<{data: {status: string, data: {rack: RackRef & {occupancy: number, is_full: boolean}}}}>}
 */
// `date` (the card's order_edd) also refuses a rack holding this customer's
// boxes for another delivery date (RACK_OTHER_EDD).
export const resolveRack = ({ code, cx_id, date }) =>
  api.post(
    "/leader/receiving/racks/resolve",
    cx_id ? { code, cx_id, ...(date ? { date } : {}) } : { code },
  );

/**
 * `POST /api/leader/receiving/tps/:cx_id/scan` — scan (or manually confirm)
 * one box onto a rack. Idempotent for a box already on that rack; a box on
 * another rack for this TP is moved. Returns the same payload as
 * `getReceivingTpDetail`.
 * @param {{cx_id: string, date?: string, rack_id: number, shipment_id: string, scan_kind: "SCANNED"|"MANUAL"}} params
 * @returns {Promise<{data: {status: string, data: ReceivingTpDetail}}>}
 */
export const scanReceivingShipment = ({
  cx_id,
  date,
  rack_id,
  shipment_id,
  scan_kind,
}) =>
  api.post(`/leader/receiving/tps/${cx_id}/scan`, {
    date,
    rack_id,
    shipment_id,
    scan_kind,
  });

/**
 * `POST /api/leader/receiving/racks/:rack_id/full` — mark a rack full (or
 * un-mark it).
 * @param {{rack_id: number, is_full: boolean}} params
 * @returns {Promise<{data: {status: string, data: {rack: RackRef & {is_full: boolean}}}}>}
 */
export const markRackFull = ({ rack_id, is_full }) =>
  api.post(`/leader/receiving/racks/${rack_id}/full`, { is_full });

/**
 * `POST /api/leader/receiving/tps/:cx_id/complete` — requires every
 * DELIVERED box on the date card racked; packs that card's orders.
 * @param {{cx_id: string, date?: string}} params
 * @returns {Promise<{data: {status: string, data: {status: "RACKED", racks: RackRef[], packed_order_ids: number[]}}}>}
 */
export const completeReceivingTp = ({ cx_id, date }) =>
  api.post(`/leader/receiving/tps/${cx_id}/complete`, { date });
